import { useState, useEffect } from 'react';
import { axiosInstance } from '@/App';
import { useNavigate } from 'react-router-dom';
import { Layout } from '@/components/Layout';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { BookOpen, FileText, Calendar, Download, Settings } from 'lucide-react';
import { format } from 'date-fns';
import { es } from 'date-fns/locale';

function ContabilidadAsientos({ user }) {
  const navigate = useNavigate();
  const [asientos, setAsientos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedAsiento, setSelectedAsiento] = useState(null);

  useEffect(() => {
    loadAsientos();
  }, []);

  const loadAsientos = async () => {
    try {
      const response = await axiosInstance.get('/contabilidad/asientos');
      setAsientos(response.data.asientos || []);
    } catch (error) {
      console.error('Error cargando asientos:', error);
      toast.error('Error al cargar los asientos contables');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateStr) => {
    try {
      return format(new Date(dateStr), 'dd/MM/yyyy', { locale: es });
    } catch {
      return dateStr;
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('es-ES', {
      style: 'currency',
      currency: 'EUR'
    }).format(amount || 0);
  };

  const getTipoDocumentoBadge = (tipo) => {
    const tipos = {
      factura_emitida: { label: 'Factura Emitida', color: 'bg-green-100 text-green-800' },
      factura_recibida: { label: 'Factura Recibida', color: 'bg-blue-100 text-blue-800' },
      manual: { label: 'Manual', color: 'bg-gray-100 text-gray-800' }
    };
    const config = tipos[tipo] || tipos.manual;
    return <Badge className={config.color}>{config.label}</Badge>;
  };

  const calcularTotales = (lineas) => {
    const totalDebe = lineas.reduce((sum, linea) => sum + (linea.debe || 0), 0);
    const totalHaber = lineas.reduce((sum, linea) => sum + (linea.haber || 0), 0);
    return { debe: totalDebe, haber: totalHaber };
  };

  if (loading) {
    return (
      <Layout user={user}>
        <div className="flex items-center justify-center h-64">
          <div className="text-gray-500">Cargando asientos contables...</div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout user={user}>
      <div className="p-6">
        <div className="mb-6">
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-3">
              <BookOpen className="h-8 w-8 text-blue-600" />
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-gray-900">
                Contabilidad C10
              </h1>
            </div>
            <div className="flex gap-2">
              <Button
                onClick={() => navigate('/contabilidad/fiscal')}
                variant="outline"
                size="sm"
              >
                Dashboard
              </Button>
              <Button
                onClick={() => navigate('/contabilidad/facturas-recibidas')}
                variant="outline"
                size="sm"
              >
                Fact. Recibidas
              </Button>
              <Button
                onClick={() => navigate('/contabilidad/libros')}
                variant="outline"
                size="sm"
              >
                Libros
              </Button>
              <Button
                onClick={() => navigate('/contabilidad/ocr')}
                variant="outline"
                size="sm"
              >
                OCR
              </Button>
              <Button
                onClick={() => navigate('/contabilidad/config')}
                variant="outline"
                size="sm"
              >
                <Settings className="h-4 w-4" />
              </Button>
            </div>
          </div>
          <p className="text-base text-gray-600">
            Asientos Contables - Registro automático de todas las operaciones
          </p>
        </div>

        {asientos.length === 0 ? (
          <Card>
            <CardContent className="flex flex-col items-center justify-center py-12">
              <FileText className="h-16 w-16 text-gray-300 mb-4" />
              <h3 className="text-lg font-semibold text-gray-900 mb-2">
                No hay asientos contables
              </h3>
              <p className="text-gray-500 text-center max-w-md">
                Los asientos contables se generarán automáticamente cuando apruebes facturas.
                Asegúrate de tener la configuración contable activada.
              </p>
            </CardContent>
          </Card>
        ) : (
          <div className="grid grid-cols-1 gap-4">
            {asientos.map((asiento) => {
              const totales = calcularTotales(asiento.lineas || []);
              const cuadra = Math.abs(totales.debe - totales.haber) < 0.01;

              return (
                <Card
                  key={asiento.id}
                  className="hover:shadow-md transition-shadow cursor-pointer"
                  onClick={() => setSelectedAsiento(selectedAsiento?.id === asiento.id ? null : asiento)}
                >
                  <CardHeader>
                    <div className="flex items-start justify-between">
                      <div>
                        <CardTitle className="flex items-center gap-3">
                          <span>Asiento Nº {asiento.numero}</span>
                          {getTipoDocumentoBadge(asiento.tipo_documento)}
                          {cuadra ? (
                            <Badge className="bg-green-100 text-green-800">Cuadrado</Badge>
                          ) : (
                            <Badge className="bg-red-100 text-red-800">Descuadrado</Badge>
                          )}
                        </CardTitle>
                        <CardDescription className="mt-1">
                          <div className="flex items-center gap-4 text-sm">
                            <span className="flex items-center gap-1">
                              <Calendar className="h-3 w-3" />
                              {formatDate(asiento.fecha)}
                            </span>
                            {asiento.documento_numero && (
                              <span className="flex items-center gap-1">
                                <FileText className="h-3 w-3" />
                                {asiento.documento_numero}
                              </span>
                            )}
                          </div>
                        </CardDescription>
                      </div>
                      <div className="text-right">
                        <div className="text-sm text-gray-500">Total</div>
                        <div className="text-lg font-semibold">
                          {formatCurrency(totales.debe)}
                        </div>
                      </div>
                    </div>
                  </CardHeader>

                  {selectedAsiento?.id === asiento.id && (
                    <CardContent>
                      <div className="border-t pt-4">
                        <div className="mb-3">
                          <h4 className="font-semibold text-gray-900 mb-1">Concepto:</h4>
                          <p className="text-gray-700">{asiento.concepto}</p>
                        </div>

                        <div className="overflow-x-auto">
                          <table className="w-full text-sm">
                            <thead>
                              <tr className="border-b">
                                <th className="text-left py-2 px-2">Cuenta</th>
                                <th className="text-left py-2 px-2">Concepto</th>
                                <th className="text-right py-2 px-2">Debe</th>
                                <th className="text-right py-2 px-2">Haber</th>
                              </tr>
                            </thead>
                            <tbody>
                              {asiento.lineas?.map((linea, idx) => (
                                <tr key={idx} className="border-b last:border-0">
                                  <td className="py-2 px-2 font-mono text-xs">{linea.cuenta}</td>
                                  <td className="py-2 px-2">{linea.concepto}</td>
                                  <td className="py-2 px-2 text-right font-medium">
                                    {linea.debe > 0 ? formatCurrency(linea.debe) : '-'}
                                  </td>
                                  <td className="py-2 px-2 text-right font-medium">
                                    {linea.haber > 0 ? formatCurrency(linea.haber) : '-'}
                                  </td>
                                </tr>
                              ))}
                              <tr className="font-bold bg-gray-50">
                                <td colSpan="2" className="py-2 px-2">TOTALES</td>
                                <td className="py-2 px-2 text-right">{formatCurrency(totales.debe)}</td>
                                <td className="py-2 px-2 text-right">{formatCurrency(totales.haber)}</td>
                              </tr>
                            </tbody>
                          </table>
                        </div>

                        {asiento.notas && (
                          <div className="mt-3 p-3 bg-yellow-50 rounded text-sm">
                            <strong>Notas:</strong> {asiento.notas}
                          </div>
                        )}
                      </div>
                    </CardContent>
                  )}
                </Card>
              );
            })}
          </div>
        )}
      </div>
    </Layout>
  );
}

export default ContabilidadAsientos;
